import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors } from '@sweep-io/sweep-design';
import { NODE_WIDTH, NODE_HEIGHT } from '../../const';
import { getObjectTypeColor, objectTypeColorNames } from '../../helpers/getObjectTypeColor';
import { CSS_Z_INDEX_SWAP_OVERLAY } from '../../const/zIndex';

const defaultNodeOpacity = '4D'; // 0.3 opacity in Hex
const defaultColors = getObjectTypeColor('__default');

const getZebraGradient = (color: string, nodeOpacity: string = defaultNodeOpacity) => {
  const transparentZebraColor = `${color}${nodeOpacity}`;
  return `repeating-linear-gradient(60deg, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 7.5px,${transparentZebraColor} 7.5px,${transparentZebraColor} 15px)`;
};

const getColorsClassesForObjectTypes = (): any => {
  const allColorCss = objectTypeColorNames.map((objectType) => {
    const colors = getObjectTypeColor(objectType);
    return {
      [`&.node-${objectType}`]: {
        backgroundColor: colors.step,
      },
      [`&.nb-node.node-${objectType}`]: {
        backgroundImage: getZebraGradient(colors.light),
      },
    };
  });
  return allColorCss.reduce((prev, curr) => {
    prev = { ...prev, ...curr };
    return prev;
  }, {});
};

const colorClasses = getColorsClassesForObjectTypes();

export const BasedNodeComponent = styled(Box)({
  width: `${NODE_WIDTH}px`,
  height: `${NODE_HEIGHT}px`,
  borderRadius: '4px',
  position: 'relative',
  cursor: 'pointer',
  justifyContent: 'space-between',
  padding: '2px', // to avoid flickering on hover
  '&.highlighted': {
    border: '2px solid ',
    borderColor: colors.blue[500],
    padding: 0,
    '&.shadow': {
      boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.1)',
    },
  },
  '&.shadow': {
    boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.1), inset 0px -2px 0px rgba(0, 0, 0, 0.15)',
  },
  '&.has-opacity': {
    opacity: 0.4,
  },
});

export const NodeComponent = styled(BasedNodeComponent)({
  background: defaultColors.step,

  '&.nb-node': {
    position: 'relative',
    backgroundImage: getZebraGradient(defaultColors.connection, '1A'),
  },
  ...colorClasses,
  '&.invalid-position': {
    borderColor: colors.blush[500],
    background: colors.blush[500],
  },
  '&.highlighted.invalid-position': {
    borderColor: colors.blush[500],
  },
  '&.swap-step-target': {
    border: '2px solid transparent',
    cursor: 'default',
    background: 'rgba(0,0,0,0.04)',

    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      border: `2px solid ${colors.blue[500]}`,
      borderRadius: '4px',
      cursor: 'default',
      background: `${colors.blue[500]}33`,
      zIndex: CSS_Z_INDEX_SWAP_OVERLAY,
    },
  },
  '&.swap-step-source': {
    opacity: 0.3,
    boxShadow: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      border: `2px solid ${colors.blue[500]}`,
      borderRadius: '4px',
      cursor: 'default',
      background: `${colors.blue[500]}33`,
      opacity: 0.3,
      zIndex: CSS_Z_INDEX_SWAP_OVERLAY,
    },
  },
});

export const InnerNodeComponent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  height: '100%',
  padding: '0 27px 0 12px',
  gap: '4px',
});
